import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Avatar from 'react-avatar';
import Cookies from 'js-cookie';
import socket from '../../../../socket';
import NotificationIcon from '../../assets/svg/Bell.svg';
import NotifyBellIcon from '../../assets/svg/NotifyBell.svg';
import {
  getBusinessNotifications,
  getNotifications,
  getNotificationUnreadCount,
  getUserById,
  getUserCompanyIds,
  markAllNotificationsRead,
  markNotificationRead
} from '../../../builder/services/api/implementation/impl';
import {
  notificationLink,
  priorityDotClass,
  relativeTime
} from '../../utils/notificationLinks';

/**
 * One header bell — the original Bell.svg / NotifyBell.svg assets.
 *
 * Default panel is business notifications (leads, payments, tasks). Activity
 * is the social live-feed that this icon already opened, kept as a second tab
 * so posts / comments / likes are not dropped.
 */

const UNREAD_COUNT_KEY = ['notifications', 'unread-count'];
const RECENT_KEY = ['notifications', 'recent'];
const SOCIAL_KEY = ['getNotifications'];

const formatSocialTime = (updatedAt) => {
  const diffInMilliseconds = Date.now() - new Date(updatedAt).getTime();
  const diffInMinutes = Math.max(0, Math.floor(diffInMilliseconds / (1000 * 60)));
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  if (diffInHours >= 24) {
    const days = Math.floor(diffInHours / 24);
    const remainingHours = diffInHours % 24;
    return `${days}d${remainingHours > 0 ? ` ${remainingHours}h` : ''}`;
  }
  if (diffInHours >= 1) return `${diffInHours}h`;
  return `${diffInMinutes}m`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('updates');
  const [socialNotify, setSocialNotify] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const { data: countData } = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: getNotificationUnreadCount,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    retry: 1
  });

  const { data: listData, isLoading } = useQuery({
    queryKey: RECENT_KEY,
    queryFn: () => getBusinessNotifications({ page: 1, limit: 10 }),
    enabled: isOpen && tab === 'updates',
    retry: 1
  });

  const { data: socialData } = useQuery({
    queryKey: SOCIAL_KEY,
    queryFn: getNotifications,
    enabled: isOpen && tab === 'activity',
    retry: 1
  });

  const unreadTotal = countData?.data?.total ?? 0;
  const notifications = Array.isArray(listData?.data) ? listData.data : [];
  const socialNotifications = Array.isArray(socialData?.data) ? socialData.data : [];
  const hasUnread = unreadTotal > 0 || socialNotify;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    queryClient.invalidateQueries({ queryKey: RECENT_KEY });
  };

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidate
  });
  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: invalidate
  });

  useEffect(() => {
    if (!isOpen) return undefined;
    const onClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    const currentUserId = () => String(Cookies.get('userId') || '');

    const handlePost = async (res) => {
      try {
        const userId = Cookies.get('userId');
        if (!userId) return;
        const userResponse = await getUserById(userId);
        const companyIds = getUserCompanyIds(userResponse);
        const postCompanyId = Number(res.company);
        if (
          companyIds.includes(postCompanyId) ||
          companyIds.includes(res.company) ||
          (userResponse?.companies || []).includes(res.company)
        ) {
          setSocialNotify(true);
        }
      } catch (error) {
        console.error('Error fetching user details in handlePost:', error);
      }
    };

    const markIfMine = (createdBy) => {
      if (currentUserId() && currentUserId() === String(createdBy)) {
        setSocialNotify(true);
      }
    };

    const handleComment = (res) => markIfMine(res.post?.created_by);
    const handleReply = (res) => markIfMine(res.comment?.created_by);
    const handleLike = (res) => markIfMine(res.post?.created_by);

    const handleTagInPost = () => {};
    const handleTagInComment = () => {};

    socket.on('Post', handlePost);
    socket.on('Comment', handleComment);
    socket.on('Reply', handleReply);
    socket.on('Like', handleLike);
    socket.on('TAG IN POST', handleTagInPost);
    socket.on('TAG IN COMMENT', handleTagInComment);

    return () => {
      socket.off('Post', handlePost);
      socket.off('Comment', handleComment);
      socket.off('Reply', handleReply);
      socket.off('Like', handleLike);
      socket.off('TAG IN POST', handleTagInPost);
      socket.off('TAG IN COMMENT', handleTagInComment);
    };
  }, []);

  const openNotification = (notification) => {
    if (!notification.read_at) markRead.mutate(notification.id);
    const link = notificationLink(notification);
    setIsOpen(false);
    if (link) navigate(link);
  };

  const openSocial = (notification) => {
    setSocialNotify(false);
    setIsOpen(false);
    navigate(`/builder/post/${notification.id}`);
  };

  const toggleOpen = () => {
    setIsOpen((open) => !open);
    setSocialNotify(false);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={
          unreadTotal > 0 ? `Notifications, ${unreadTotal} unread` : 'Notifications'
        }
        aria-expanded={isOpen}
        onClick={toggleOpen}
        className="relative flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#49A4AB]">
        <img
          className="inline-block h-8 w-8 cursor-pointer"
          src={hasUnread ? NotifyBellIcon : NotificationIcon}
          alt=""
        />
        {unreadTotal > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white">
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notifications"
            className="fixed right-4 top-16 z-[10000] w-96 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-4 pt-3">
              <h3 className="pb-3 text-base font-semibold text-gray-900">Notifications</h3>
              {tab === 'updates' && unreadTotal > 0 && (
                <button
                  type="button"
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                  className="pb-3 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50">
                  Mark all as read
                </button>
              )}
            </div>

            <div className="flex border-b px-2">
              <button
                type="button"
                onClick={() => setTab('updates')}
                className={`flex-1 px-3 py-2 text-sm font-medium ${
                  tab === 'updates'
                    ? 'border-b-2 border-[#346C75] text-[#346C75]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}>
                Updates
                {unreadTotal > 0 ? ` (${unreadTotal > 99 ? '99+' : unreadTotal})` : ''}
              </button>
              <button
                type="button"
                onClick={() => {
                  setTab('activity');
                  setSocialNotify(false);
                }}
                className={`flex-1 px-3 py-2 text-sm font-medium ${
                  tab === 'activity'
                    ? 'border-b-2 border-[#346C75] text-[#346C75]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}>
                Activity
              </button>
            </div>

            <div className="max-h-[24rem] overflow-y-auto">
              {tab === 'updates' && (
                <>
                  {isLoading && (
                    <p className="px-4 py-8 text-center text-sm text-gray-500">Loading…</p>
                  )}

                  {!isLoading && notifications.length === 0 && (
                    <div className="px-4 py-10 text-center">
                      <p className="text-sm font-medium text-gray-700">You are all caught up</p>
                      <p className="mt-1 text-xs text-gray-500">
                        New leads, payments and task updates will show up here.
                      </p>
                    </div>
                  )}

                  {notifications.map((notification) => {
                    const isUnread = !notification.read_at;
                    return (
                      <button
                        key={notification.id}
                        type="button"
                        onClick={() => openNotification(notification)}
                        className={`flex w-full gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 ${
                          isUnread ? 'bg-blue-50/40' : ''
                        }`}>
                        <span
                          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                            isUnread ? 'bg-blue-500' : priorityDotClass(notification.priority)
                          }`}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline justify-between gap-2">
                            <span
                              className={`truncate text-sm ${
                                isUnread
                                  ? 'font-semibold text-gray-900'
                                  : 'font-medium text-gray-700'
                              }`}>
                              {notification.title}
                            </span>
                            <span className="shrink-0 text-[11px] text-gray-400">
                              {relativeTime(notification.created_at)}
                            </span>
                          </span>
                          <span className="mt-0.5 block text-xs leading-snug text-gray-600">
                            {notification.body}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </>
              )}

              {tab === 'activity' && (
                <>
                  {socialNotifications.length === 0 && (
                    <div className="px-4 py-10 text-center">
                      <p className="text-sm font-medium text-gray-700">No activity yet</p>
                      <p className="mt-1 text-xs text-gray-500">
                        Posts, comments and likes from the live feed show up here.
                      </p>
                    </div>
                  )}
                  {socialNotifications.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      onClick={() => openSocial(notification)}
                      className="flex w-full items-center gap-2.5 border-b border-[#f6f4f3] px-3 py-2 text-left hover:bg-gray-50">
                      <Avatar name={notification.user?.name} size="35" round />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-black">
                          {notification.user?.name} {notification.content}
                        </span>
                        <span className="block text-[13px] text-gray-500">
                          {formatSocialTime(notification.updated_at)}
                        </span>
                      </span>
                      <span
                        className="h-2 w-2 shrink-0 rounded-full bg-[#346C75]"
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                </>
              )}
            </div>

            {tab === 'updates' && (
              <div className="border-t px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/builder/notifications');
                  }}
                  className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700">
                  See all notifications
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default NotificationBell;
